import { Toast } from "@base-ui/react/toast";
import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import { IconX } from "@tabler/icons-react";
import { createContext, useCallback, useContext, useMemo, useState, type ReactElement, type ReactNode } from "react";
import { Dialog } from "./dialog";
import { Field } from "./ui";

/*
 * Feedback on Base UI: short-lived toasts, confirm and prompt dialogs in place of window.confirm and
 * window.prompt, and tooltips. ExtranetShell mounts KitProviders, so pages only call the hooks.
 */

// ---- Toasts ----------------------------------------------------------------------------------------

export type ToastTone = "ok" | "warn" | "bad";
type ShowToast = (text: string, tone?: ToastTone) => void;
const ToastCtx = createContext<ShowToast | undefined>(undefined);

/**
 * Shows a short message in the corner that goes away by itself (6 s; paused while the pointer or focus is
 * on it) and is announced to screen readers. For confirmations of what the person just did ("Saved").
 * Anything that must stay readable, such as a link to copy or an error to act on, belongs in a Banner.
 */
export function useToast(): ShowToast {
  return useContext(ToastCtx) ?? noToast;
}
const noToast: ShowToast = (text) => console.warn(`useToast outside KitProviders: ${text}`);

function ToastBridge({ children }: { children: ReactNode }) {
  const manager = Toast.useToastManager();
  const show = useCallback<ShowToast>((text, tone = "ok") => void manager.add({ description: text, type: tone, priority: tone === "bad" ? "high" : "low" }), [manager]);
  return <ToastCtx.Provider value={show}>{children}</ToastCtx.Provider>;
}

function ToastList() {
  const { toasts } = Toast.useToastManager();
  return toasts.map((t) => (
    <Toast.Root key={t.id} toast={t} className={`toast ${t.type ?? "ok"}`}>
      <Toast.Content className="toast-content">
        <Toast.Description className="toast-text" />
        <Toast.Close className="ghost icon toast-close" aria-label="Dismiss"><IconX size={14} /></Toast.Close>
      </Toast.Content>
    </Toast.Root>
  ));
}

// ---- Confirm ---------------------------------------------------------------------------------------

export interface ConfirmOptions {
  title: string;
  /** What will happen, under the title. */
  body?: ReactNode;
  /** The confirming button; default "Confirm". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Something that can't be undone easily (deactivate, delete): a red confirming button. */
  danger?: boolean;
}
type Confirm = (options: ConfirmOptions) => Promise<boolean>;
const ConfirmCtx = createContext<Confirm | undefined>(undefined);

/**
 * `if (!(await confirm({ title: "Deactivate ana@…?", danger: true }))) return;` — an alert dialog in the
 * kit's look instead of the browser's confirm(). Cancel has the focus first, so Enter does not confirm by
 * accident; Escape cancels. Outside KitProviders it falls back to window.confirm.
 */
export function useConfirm(): Confirm {
  return useContext(ConfirmCtx) ?? fallbackConfirm;
}
const fallbackConfirm: Confirm = async ({ title, body }) => window.confirm(typeof body === "string" ? `${title}\n\n${body}` : title);

function ConfirmHost({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<{ options: ConfirmOptions; resolve: (ok: boolean) => void }>();
  const confirm = useCallback<Confirm>((options) => new Promise<boolean>((resolve) => setPending({ options, resolve })), []);
  const answer = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(undefined);
  };
  const o = pending?.options;
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {o && (
        <Dialog
          alert
          title={o.title}
          onClose={() => answer(false)}
          actions={<>
            <button type="button" onClick={() => answer(false)}>{o.cancelLabel ?? "Cancel"}</button>
            <button type="button" className={o.danger ? "danger solid" : "primary"} onClick={() => answer(true)}>{o.confirmLabel ?? "Confirm"}</button>
          </>}
        >
          {o.body && <div className="muted">{o.body}</div>}
        </Dialog>
      )}
    </ConfirmCtx.Provider>
  );
}

// ---- Prompt ----------------------------------------------------------------------------------------

export interface PromptOptions {
  title: string;
  /** What the person types, as the field's label ("Reason"); default: the title is the only label. */
  label?: string;
  /** Under the title: what happens with the answer ("The customer reads this."). */
  body?: ReactNode;
  initial?: string;
  placeholder?: string;
  /** A longer answer (a reason, a note): a textarea instead of a one-line input. */
  multiline?: boolean;
  /** The answer may not be empty. */
  required?: boolean;
  confirmLabel?: string;
  danger?: boolean;
}
type Prompt = (options: PromptOptions) => Promise<string | null>;
const PromptCtx = createContext<Prompt | undefined>(undefined);

/**
 * `const reason = await prompt({ title: "Why is it rejected?", multiline: true }); if (reason === null) return;`
 * — a dialog with one field instead of the browser's prompt(). Resolves to the text (trimmed) or null when
 * cancelled. Outside KitProviders it falls back to window.prompt.
 */
export function usePrompt(): Prompt {
  return useContext(PromptCtx) ?? fallbackPrompt;
}
const fallbackPrompt: Prompt = async ({ title, initial }) => {
  const v = window.prompt(title, initial ?? "");
  return v === null ? null : v.trim();
};

function PromptHost({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<{ options: PromptOptions; resolve: (v: string | null) => void }>();
  const [text, setText] = useState("");
  const prompt = useCallback<Prompt>((options) => new Promise<string | null>((resolve) => {
    setText(options.initial ?? "");
    setPending({ options, resolve });
  }), []);
  const answer = (v: string | null) => {
    pending?.resolve(v);
    setPending(undefined);
  };
  const o = pending?.options;
  const blocked = Boolean(o?.required) && !text.trim();
  return (
    <PromptCtx.Provider value={prompt}>
      {children}
      {o && (
        <Dialog
          title={o.title}
          description={o.body}
          onClose={() => answer(null)}
          onSubmit={() => !blocked && answer(text.trim())}
          actions={<>
            <button type="button" onClick={() => answer(null)}>Cancel</button>
            <button type="submit" className={o.danger ? "danger solid" : "primary"} disabled={blocked}>{o.confirmLabel ?? "OK"}</button>
          </>}
        >
          <Field label={o.label ?? o.title}>
            {o.multiline
              ? <textarea rows={3} value={text} placeholder={o.placeholder} onChange={(e) => setText(e.target.value)} autoFocus />
              : <input value={text} placeholder={o.placeholder} onChange={(e) => setText(e.target.value)} autoFocus />}
          </Field>
        </Dialog>
      )}
    </PromptCtx.Provider>
  );
}

// ---- Tooltip ---------------------------------------------------------------------------------------

/**
 * A short label shown on hover and on keyboard focus (unlike `title`, which keyboards and touch screens
 * never show). Wrap one focusable element: `<Tooltip content="Sign out"><button aria-label="Sign out">…`.
 * Screen readers do not read the tooltip, so an icon-only button still needs its aria-label.
 */
export function Tooltip({ content, children, side = "top" }: { content: ReactNode; children: ReactElement; side?: "top" | "bottom" | "left" | "right" }) {
  return (
    <BaseTooltip.Root>
      <BaseTooltip.Trigger render={children} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner className="tip-positioner" side={side} sideOffset={6}>
          <BaseTooltip.Popup className="tip">{content}</BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}

// ---- Providers -------------------------------------------------------------------------------------

/** Toasts, confirm dialogs and tooltips for everything inside; ExtranetShell already wraps the page in it. */
export function KitProviders({ children }: { children: ReactNode }) {
  const toasts = useMemo(() => (
    <Toast.Portal>
      <Toast.Viewport className="toast-viewport"><ToastList /></Toast.Viewport>
    </Toast.Portal>
  ), []);
  return (
    <Toast.Provider timeout={6000} limit={3}>
      <BaseTooltip.Provider delay={400}>
        <ToastBridge>
          <ConfirmHost><PromptHost>{children}</PromptHost></ConfirmHost>
        </ToastBridge>
      </BaseTooltip.Provider>
      {toasts}
    </Toast.Provider>
  );
}
